import { useEffect, useRef, useState } from 'react'
import jsQR from 'jsqr'

interface QrScannerProps {
  onDetect: (conteudo: string) => void
}

interface DetectorQr {
  detect: (fonte: CanvasImageSource) => Promise<{ rawValue: string }[]>
}

type ConstrutorDetector = new (opcoes: { formats: string[] }) => DetectorQr

/** Lê QR Code pela câmera: usa o detector nativo quando existe e cai para jsQR quando não. */
export function QrScanner({ onDetect }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [lendo, setLendo] = useState(false)
  const [dica, setDica] = useState<string | null>(null)

  // Guardado em ref: assim trocar de callback não reinicia a câmera.
  const onDetectRef = useRef(onDetect)
  useEffect(() => { onDetectRef.current = onDetect }, [onDetect])

  useEffect(() => {
    let parado = false
    let stream: MediaStream | null = null
    let frame = 0
    const canvas = document.createElement('canvas')

    const encerrar = () => stream?.getTracks().forEach((t) => t.stop())

    async function iniciar() {
      let capturada: MediaStream
      try {
        // Resolução alta ajuda: o QR do cupom é pequeno dentro do quadro.
        capturada = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        })
      } catch {
        if (!parado) setErro('Não foi possível acessar a câmera. Autorize o uso no navegador ou use as outras opções.')
        return
      }

      // Em desenvolvimento o efeito roda duas vezes; sem isso a primeira captura ficaria ligada.
      if (parado || !videoRef.current) {
        capturada.getTracks().forEach((t) => t.stop())
        return
      }

      stream = capturada
      const video = videoRef.current
      video.srcObject = capturada

      try {
        await video.play()
      } catch (e) {
        if ((e as Error).name !== 'AbortError' && !parado) {
          setErro('A câmera abriu, mas o vídeo não pôde ser exibido. Tente recarregar a página.')
        }
        return
      }

      if (parado) return
      setLendo(true)

      const Detector = (window as unknown as { BarcodeDetector?: ConstrutorDetector }).BarcodeDetector
      const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null

      let tentativas = 0

      const lerRecorte = (v: HTMLVideoElement, apenasCentro: boolean): string | null => {
        const lado = Math.min(v.videoWidth, v.videoHeight)
        const origem = apenasCentro
          ? { sx: (v.videoWidth - lado * 0.6) / 2, sy: (v.videoHeight - lado * 0.6) / 2, sw: lado * 0.6, sh: lado * 0.6 }
          : { sx: 0, sy: 0, sw: v.videoWidth, sh: v.videoHeight }

        canvas.width = Math.round(origem.sw)
        canvas.height = Math.round(origem.sh)
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) return null

        ctx.drawImage(v, origem.sx, origem.sy, origem.sw, origem.sh, 0, 0, canvas.width, canvas.height)
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
        return jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' })?.data ?? null
      }

      const tick = async () => {
        if (parado || !videoRef.current) return
        const v = videoRef.current

        if (v.readyState === v.HAVE_ENOUGH_DATA && v.videoWidth > 0) {
          try {
            if (detector) {
              const [achado] = await detector.detect(v)
              if (achado?.rawValue) { onDetectRef.current(achado.rawValue); return }
            }

            // Alterna quadro inteiro e recorte central: no recorte o QR ocupa mais pixels.
            const achado = lerRecorte(v, tentativas % 2 === 1)
            if (achado) { onDetectRef.current(achado); return }
          } catch {
            // quadro ruim: tenta no próximo
          }

          tentativas++
          if (tentativas === 60) setDica('Aproxime o cupom até o QR preencher o quadrado, e mantenha firme.')
          if (tentativas === 180) setDica('Se não ler, use "Digitar chave": os 44 dígitos estão impressos no cupom.')
        }

        frame = requestAnimationFrame(tick)
      }

      frame = requestAnimationFrame(tick)
    }

    iniciar()

    return () => {
      parado = true
      cancelAnimationFrame(frame)
      encerrar()
    }
  }, [])

  if (erro) {
    return (
      <div className="flex items-center gap-2 rounded bg-expense/10 px-3 py-2.5 text-13 text-expense" role="alert">
        <i className="ri-camera-off-line" aria-hidden /> {erro}
      </div>
    )
  }

  return (
    <div>
      <div className="relative overflow-hidden rounded bg-black">
        <video ref={videoRef} className="aspect-video w-full object-cover" muted playsInline />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="aspect-square h-3/5 rounded border-2 border-primary/80" />
        </div>
        <p className="absolute inset-x-0 bottom-0 bg-black/60 py-1.5 text-center text-2xs text-white">
          {lendo ? 'Encaixe o QR Code dentro do quadrado' : 'Iniciando câmera...'}
        </p>
      </div>
      {dica && (
        <p className="mt-2 flex items-center gap-1.5 rounded bg-warning/10 px-3 py-2 text-13 text-warning">
          <i className="ri-information-line" aria-hidden /> {dica}
        </p>
      )}
    </div>
  )
}

/**
 * Procura um QR Code numa imagem já salva. Foto de celular costuma ser grande demais
 * para o detector, então tenta em algumas reduções antes de desistir.
 */
export async function lerQrDaImagem(arquivo: File): Promise<string | null> {
  const bitmap = await createImageBitmap(arquivo)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  const maiorLado = Math.max(bitmap.width, bitmap.height)
  const escalas = [1, 1600 / maiorLado, 1200 / maiorLado, 2400 / maiorLado, 800 / maiorLado]

  for (const escala of escalas) {
    if (!Number.isFinite(escala) || escala <= 0 || escala > 1.5) continue

    canvas.width = Math.round(bitmap.width * escala)
    canvas.height = Math.round(bitmap.height * escala)
    if (canvas.width < 80 || canvas.height < 80) continue

    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const achado = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' })
    if (achado?.data) return achado.data
  }

  return null
}
